// hooks/useCipChartData.js
import { useMemo } from "react";

export function useCipChartData({
  renton,
  stateData,
  comparisonTest,
  selectedSchools,
  filterSession,
  districtName,
  comparisonSelection,
  nontrad
}) {
  const hasData = !!(renton?.length && stateData?.length);

  // Helper to check if a CIP matches the current filter
  const matchesFilter = (item) => {
    if (filterSession === "nontrad-female") {
      return nontrad?.female.includes(item.cipcode);
    } else if (filterSession === "nontrad-male") {
      return nontrad?.male.includes(item.cipcode);
    }
    return item.clusterName === filterSession || item.programArea === filterSession;
  };

  // ----- 1) State denominator: sum all CIP codes in filterSession for 'Statewide'
  const stateDenominator = useMemo(() => {
    if (!hasData) return [];
    const clusterItems = stateData.filter(matchesFilter);
    // Gather all .enrolled arrays for districtName==="Statewide"
    const arrays = clusterItems.flatMap(item =>
      item.enrollment
        .filter(d => d.districtName === "Statewide")
        .map(d => d.enrolled) // array of yearly enrollments
    );
    // Sum them year by year
    const sumArr = arrays.reduce((acc, arr) => {
      arr.forEach((val, i) => {
        acc[i] = (acc[i] || 0) + val;
      });
      return acc;
    }, []);
    return sumArr;
  }, [hasData, filterSession, stateData, nontrad]);

  // ----- 2) District (school) denominator: sum all CIP codes for selected schools
  const schoolDenominator = useMemo(() => {
    if (!hasData) return [];
    const selectedSch = selectedSchools.map(s => s.value);
    const clusterItems = renton.filter(matchesFilter);
    const arrays = clusterItems.flatMap(item =>
      item.enrolled
        .filter(e => selectedSch.includes(e.schoolName))
        .map(e => e.enrollment)
    );
    const sumArr = arrays.reduce((acc, arr) => {
      arr.forEach((val, i) => {
        acc[i] = (acc[i] || 0) + val;
      });
      return acc;
    }, []);
    return sumArr;
  }, [hasData, renton, filterSession, selectedSchools, nontrad]);

  // ----- 3) Comparison denominator
  const comparisonDenominator = useMemo(() => {
    if (!comparisonTest?.length) return [];
    // Flatten all districts
    // Then pick those matching clusterName=filterSession, gather .enrollment
    const relevant = comparisonTest
      .flatMap(arr => arr)
      .filter(matchesFilter);
    const arrays = relevant.map(x => x.enrollment);
    const sumArr = arrays.reduce((acc, arr) => {
      arr.forEach((val, i) => {
        acc[i] = (acc[i] || 0) + val;
      });
      return acc;
    }, []);
    return sumArr;
  }, [comparisonTest, filterSession, nontrad]);

  // ----- 4) Now build CIP lines
  const chartsByCip = useMemo(() => {
    if (!hasData) return {};

    // Collect all CIP codes in that cluster from renton + stateData
    const cips = new Set();
    stateData.forEach(sd => {
      if (matchesFilter(sd)) {
        cips.add(sd.cipcode);
      }
    });
    renton.forEach(rd => {
      if (matchesFilter(rd)) {
        cips.add(rd.cipcode);
      }
    });

    const selectedSch = selectedSchools.map(s => s.value);
    const results = {};

    cips.forEach(cip => {
      // CIP State line (sum CIP's enrollment, but / the "all CIP codes" stateDenominator)
      // 4A) Sum CIP's yearly enrollment from stateData
      let cipStateArr = [];
      stateData
        .filter(sd => matchesFilter(sd) && sd.cipcode === cip)
        .forEach(match => {
          match.enrollment
            .filter(d => d.districtName === "Statewide")
            .forEach(d => {
              d.enrolled.forEach((val, i) => {
                cipStateArr[i] = (cipStateArr[i] || 0) + val;
              });
            });
        });
      //  -> Then map CIP’s sum to a line
      const stateLine = cipStateArr.slice(0, 5).map((val, i) => {
        const denom = stateDenominator[i] || 0;
        return {
          x: 2021 + i,
          num: val,
          dum: denom,
          y: denom ? val / denom : 0
        };
      });

      // CIP District line
      let offered = false;
      let cipDistArr = [];
      renton
        .filter(rd => matchesFilter(rd) && rd.cipcode === cip)
        .forEach(matchingCip => {
          offered = true;
          matchingCip.enrolled
            .filter(e => selectedSch.includes(e.schoolName))
            .forEach(e => {
              e.enrollment.forEach((val, i) => {
                cipDistArr[i] = (cipDistArr[i] || 0) + val;
              });
            });
        });
        const districtLine = cipDistArr.slice(0, 5).map((val, i) => {
          const denom = schoolDenominator[i] || 0;
          return {
            x: 2021 + i,
            num: val,
            dum: denom,
            y: denom ? val / denom : null
          };
        });
        

      // CIP Comparison line
      let cipCompArr = [];
      if (comparisonTest?.length) {
        comparisonTest.forEach(districtArray => {
          // find CIP in that district
          districtArray
            .filter(x => matchesFilter(x) && x.cipcode === cip)
            .forEach(x => {
              x.enrollment.forEach((val, i) => {
                cipCompArr[i] = (cipCompArr[i] || 0) + val;
              });
            });
        });
      }
      const comparisonLine = cipCompArr.slice(0, 5).map((val, i) => {
        const denom = comparisonDenominator[i] || 0;
        return {
          x: 2021 + i,
          num: val,
          dum: denom,
          y: denom ? val / denom : null
        };
      });

      // NonTrad checks
      const isNonTradFemale = nontrad?.female.includes(cip);
      const isNonTradMale = nontrad?.male.includes(cip);

        // ----- New: Determine CIP Name from stateData or renton
  let cipName = "";
  const matchingStateData = stateData.find(
    sd =>
      matchesFilter(sd) &&
      sd.cipcode === cip &&
      sd.cipcodeName
  );
  if (matchingStateData) {
    cipName = matchingStateData.cipcodeName;
  } else {
    const matchingRentonData = renton.find(
      rd =>
        matchesFilter(rd) &&
        rd.cipcode === cip &&
        rd.cipcodeName
    );
    if (matchingRentonData) {
      cipName = matchingRentonData.cipcodeName;
    }
  }


      results[cip] = {
        cipcode: cip,
        cipName,
        offered,
        isNonTrad: isNonTradFemale || isNonTradMale,
        stateLine,
        districtLine,
        comparisonLine
      };
    });

    return results;
  }, [
    hasData,
    filterSession,
    renton,
    stateData,
    comparisonTest,
    selectedSchools,
    stateDenominator,
    schoolDenominator,
    comparisonDenominator,
    nontrad
  ]);

  // ----- 5) globalMax from all lines
  const globalMax = useMemo(() => {
    let maxVal = 0;
    Object.values(chartsByCip).forEach(({ stateLine, districtLine, comparisonLine }) => {
      [stateLine, districtLine, comparisonLine].forEach(line => {
        line.forEach(pt => {
          if (pt && pt.y !== null && pt.y > maxVal) {
            maxVal = pt.y;
          }
        });
      });
    });
    return maxVal;
  }, [chartsByCip]);

  // Quick set of CIP codes actually offered
  const offeredCips = new Set(
    Object.values(chartsByCip)
      .filter(c => c.offered)
      .map(c => c.cipcode)
  );

  return {
    chartsByCip,
    globalMax,
    offeredCips,
    hasData
  };
}